import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Settings as SettingsIcon, Key, Save, CheckCircle2, XCircle,
  Loader2, Eye, EyeOff, RefreshCw, Home, ArrowLeft
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { useMeta } from "../context/MetaContext";

export default function Settings() {
  const [language] = useState(() => localStorage.getItem("helcoo-language") || "ar");
  const [accessToken, setAccessToken] = useState("");
  const [adAccountId, setAdAccountId] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [localStatus, setLocalStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Use global Meta context
  const { metaStatus, fetchCredentials, saveCredentials, testConnection } = useMeta();

  const isArabic = language === "ar";
  
  // Get connection status from global context or local testing
  const connectionStatus = localStatus || metaStatus;

  const t = {
    pageTitle: isArabic ? "الإعدادات" : "Settings",
    pageSubtitle: isArabic ? "إدارة إعدادات Meta API" : "Manage Meta API Settings",
    metaSettings: isArabic ? "إعدادات Meta API" : "Meta API Settings",
    metaDescription: isArabic ? "أدخل بيانات حساب Meta للاتصال" : "Enter your Meta account credentials",
    accessToken: "Access Token",
    accessTokenPlaceholder: isArabic ? "أدخل Meta Access Token" : "Enter Meta Access Token",
    adAccountId: "Ad Account ID",
    adAccountIdPlaceholder: "act_123456789",
    save: isArabic ? "حفظ الإعدادات" : "Save Settings",
    saving: isArabic ? "جاري الحفظ..." : "Saving...",
    testConnection: isArabic ? "اختبار الاتصال" : "Test Connection",
    testing: isArabic ? "جاري الاختبار..." : "Testing...",
    connectionSuccess: isArabic ? "الاتصال ناجح!" : "Connection successful!",
    connectionFailed: isArabic ? "فشل الاتصال" : "Connection failed",
    settingsSaved: isArabic ? "تم حفظ الإعدادات بنجاح!" : "Settings saved!",
    settingsError: isArabic ? "خطأ في حفظ الإعدادات" : "Error saving settings",
    currentStatus: isArabic ? "الحالة الحالية" : "Current Status",
    connected: isArabic ? "متصل" : "Connected",
    disconnected: isArabic ? "غير متصل" : "Disconnected",
    accountName: isArabic ? "اسم الحساب" : "Account Name",
    backToHome: isArabic ? "العودة للرئيسية" : "Back to Home",
    howToGetToken: isArabic ? "كيف أحصل على Access Token؟" : "How to get Access Token?",
    step1: isArabic ? "اذهب إلى Meta Business Suite" : "Go to Meta Business Suite",
    step2: isArabic ? "أو استخدم Graph API Explorer" : "Or use Graph API Explorer",
    step3: isArabic ? "اختر الصلاحيات: ads_read, ads_management" : "Select permissions: ads_read, ads_management",
    step4: isArabic ? "انسخ التوكن الناتج" : "Copy the generated token",
    fillAllFields: isArabic ? "يرجى ملء جميع الحقول" : "Please fill all fields",
    enterTokenFirst: isArabic ? "يرجى إدخال Access Token أولاً" : "Please enter Access Token first"
  };

  // Load settings on mount using context
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await fetchCredentials();
      if (data) {
        setAccessToken(data.access_token || "");
        setAdAccountId(data.ad_account_id || "");
        if (data.connected !== undefined) {
          setLocalStatus({ connected: data.connected, account_name: data.account_name });
        }
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!accessToken.trim()) {
      toast.error(t.enterTokenFirst);
      return;
    }

    setTesting(true);
    try {
      const result = await testConnection(accessToken, adAccountId);
      
      if (result.valid) {
        setLocalStatus({ 
          connected: true, 
          account_name: result.account_name
        });
        toast.success(`${t.connectionSuccess} - ${result.account_name || ""}`);
      } else {
        setLocalStatus({ connected: false });
        toast.error(`${t.connectionFailed}: ${result.error || ""}`);
      }
    } catch (error) {
      setLocalStatus({ connected: false });
      toast.error(t.connectionFailed);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!accessToken.trim() || !adAccountId.trim()) {
      toast.error(t.fillAllFields);
      return;
    }

    setSaving(true);
    try {
      const result = await saveCredentials(accessToken, adAccountId);
      
      if (result.success) {
        setLocalStatus({ 
          connected: true, 
          account_name: result.account_name 
        });
        toast.success(t.settingsSaved);
      } else {
        toast.error(result.error || t.settingsError);
      }
    } catch (error) {
      toast.error(t.settingsError);
    } finally {
      setSaving(false);
    }
  };

  const goHome = () => {
    window.location.href = "/";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8" dir={isArabic ? "rtl" : "ltr"}>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <SettingsIcon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              {t.pageTitle}
            </h1>
            <p className="text-muted-foreground mt-1">{t.pageSubtitle}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={goHome}
            className="gap-2"
          >
            {isArabic ? <ArrowLeft className="w-4 h-4" /> : null}
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline">{t.backToHome}</span>
            {!isArabic ? <ArrowLeft className="w-4 h-4 rotate-180" /> : null}
          </Button>
        </div>

        {/* Connection Status Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              {t.currentStatus}
              <Badge 
                variant={connectionStatus?.connected ? "default" : "secondary"}
                className={connectionStatus?.connected ? "bg-green-600" : "bg-red-500"}
              >
                {connectionStatus?.connected ? (
                  <><CheckCircle2 className="w-3 h-3 mr-1" />{t.connected}</>
                ) : (
                  <><XCircle className="w-3 h-3 mr-1" />{t.disconnected}</>
                )}
              </Badge>
            </CardTitle>
          </CardHeader>
          {connectionStatus?.connected && connectionStatus?.account_name && (
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground">
                {t.accountName}: <span className="font-medium text-foreground">{connectionStatus.account_name}</span>
              </p>
            </CardContent>
          )}
        </Card>

        {/* Meta Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              {t.metaSettings}
            </CardTitle>
            <CardDescription>{t.metaDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Access Token */}
            <div className="space-y-2">
              <Label htmlFor="access-token">{t.accessToken}</Label>
              <div className="relative">
                <Input
                  id="access-token"
                  type={showToken ? "text" : "password"}
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  placeholder={t.accessTokenPlaceholder}
                  className={isArabic ? "pl-10" : "pr-10"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={`absolute top-1/2 -translate-y-1/2 h-7 w-7 p-0 ${isArabic ? 'left-1' : 'right-1'}`}
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Ad Account ID */}
            <div className="space-y-2">
              <Label htmlFor="ad-account-id">{t.adAccountId}</Label>
              <Input
                id="ad-account-id"
                type="text"
                value={adAccountId}
                onChange={(e) => setAdAccountId(e.target.value)}
                placeholder={t.adAccountIdPlaceholder}
              />
            </div>

            <Separator />

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleTestConnection}
                variant="outline"
                disabled={testing || !accessToken.trim()}
                className="flex-1 gap-2"
              >
                {testing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />{t.testing}</>
                ) : (
                  <><RefreshCw className="w-4 h-4" />{t.testConnection}</>
                )}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !accessToken.trim() || !adAccountId.trim()}
                className="flex-1 gap-2"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />{t.saving}</>
                ) : (
                  <><Save className="w-4 h-4" />{t.save}</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t.howToGetToken}</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center mt-0.5">1</span>
                <span>{t.step1}: <a href="https://business.facebook.com/settings" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">business.facebook.com</a></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center mt-0.5">2</span>
                <span>{t.step2}: <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Graph API Explorer</a></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center mt-0.5">3</span>
                <span>{t.step3}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center mt-0.5">4</span>
                <span>{t.step4}</span>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
